import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../Layout/Layout';
import { Form, FormGroup, Label, Input, Button, ButtonGroup } from '../common/Form';
import { subscriptionService } from '../../services/subscriptionService';
import { AuthContext } from '../../contexts/AuthContext';
import { SUBSCRIPTION_TAGS } from '../../data/constants';
import { tagService } from '../../services/tagService';

const Container = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 24px;
  color: var(--text-primary);
`;

const TagsWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const TagsInput = styled.div`
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const TagItem = styled.span`
  background: var(--primary);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RemoveTag = styled.span`
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const TagsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
`;

const TagOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  color: var(--text-primary);
  background: ${props => props.selected ? 'var(--hover)' : 'transparent'};

  &:hover {
    background: var(--hover);
  }
`;

const TagSection = styled.div`
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TagSectionTitle = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PlaceholderText = styled.span`
  color: var(--text-secondary);
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 16px;
  background: var(--surface);
  color: var(--text-primary);
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  &:disabled {
    background: var(--surface-hover);
    cursor: not-allowed;
  }
`;

const EditSubscription = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState({
    name: '',
    provider: '',
    price: '',
    cycle: '',
    payment_method: '',
    start_date: '',
    tags: [],
    user_id: user?.id
  });
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);
  const [userTags, setUserTags] = useState([]);
  const tagsRef = useRef(null);

  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const data = await subscriptionService.getSubscriptionById(id);
        setSubscription(data);
      } catch (error) {
        console.error('Error loading subscription:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserTags = async () => {
      if (!user) return;
      
      try {
        const userCreatedTags = await tagService.getAllTags(user.id);
        
        const formattedUserTags = userCreatedTags.map(tag => ({
          value: tag.name.toLowerCase(),
          label: tag.name,
          isUserTag: true
        }));

        const allTags = [...formattedUserTags, ...SUBSCRIPTION_TAGS];
        
        const uniqueTags = allTags.filter((tag, index, self) =>
          index === self.findIndex(t => t.value === tag.value)
        );
        
        setUserTags(uniqueTags);
      } catch (error) {
        console.error('Error fetching tags:', error);
        setUserTags(SUBSCRIPTION_TAGS);
      }
    };

    if (id && user) {
      loadSubscription();
    }

    fetchUserTags();
  }, [id, user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagsRef.current && !tagsRef.current.contains(event.target)) {
        setShowTagsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      // Ensure user_id is included
      const updateData = {
        ...subscription,
        user_id: user.id
      };

      console.log('Submitting update with data:', updateData);

      await subscriptionService.updateSubscription(id, updateData);
      
      // Navigate back to subscriptions page after successful update
      navigate('/subscriptions', { replace: true });
      
    } catch (error) {
      console.error('Error updating subscription:', error);
      setError(error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSubscription(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagSelect = (tagValue) => {
    setSubscription(prev => {
      const newTags = prev.tags.includes(tagValue)
        ? prev.tags.filter(t => t !== tagValue)
        : [...prev.tags, tagValue];
      
      return {
        ...prev,
        tags: newTags
      };
    });
  };

  const removeTag = (tagToRemove) => {
    setSubscription(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Layout>
      <Container>
        <Header>
          <Title>Edit Subscription</Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Name</Label>
            <Input
              type="text"
              name="name"
              value={subscription.name}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Provider</Label>
            <Input
              type="text"
              name="provider"
              value={subscription.provider}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Price</Label>
            <Input
              type="number"
              name="price"
              value={subscription.price}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Cycle</Label>
            <Select
              name="cycle"
              value={subscription.cycle}
              onChange={handleChange}
              required
            >
              <option value="">Select cycle</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Payment Method</Label>
            <Select
              name="payment_method"
              value={subscription.payment_method}
              onChange={handleChange}
              required
            >
              <option value="">Select payment method</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Debit Card">Debit Card</option>
              <option value="PayPal">PayPal</option>
              
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Start Date</Label>
            <Input
              type="date"
              name="start_date"
              value={subscription.start_date?.split('T')[0]}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Tags</Label>
            <TagsWrapper ref={tagsRef}>
              <TagsInput onClick={() => setShowTagsDropdown(!showTagsDropdown)}>
                {Array.isArray(subscription.tags) && subscription.tags.length > 0 ? (
                  subscription.tags.map(tagValue => {
                    const tag = userTags.find(t => t.value === tagValue);
                    return (
                      <TagItem key={tagValue}>
                        {tag?.label || tagValue}
                        <RemoveTag
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTag(tagValue);
                          }}
                        >
                          ×
                        </RemoveTag>
                      </TagItem>
                    );
                  })
                ) : (
                  <PlaceholderText>Select tags...</PlaceholderText>
                )}
              </TagsInput>

              {showTagsDropdown && (
                <TagsDropdown>
                  {userTags.length > 0 ? (
                    <>
                      {userTags.filter(tag => tag.isUserTag).length > 0 && (
                        <TagSection>
                          <TagSectionTitle>Your Tags</TagSectionTitle>
                          {userTags
                            .filter(tag => tag.isUserTag)
                            .map(tag => (
                              <TagOption
                                key={tag.value}
                                selected={subscription.tags?.includes(tag.value)}
                                onClick={() => handleTagSelect(tag.value)}
                              >
                                {tag.label}
                              </TagOption>
                            ))}
                        </TagSection>
                      )}
                      
                      <TagSection>
                        <TagSectionTitle>Default Tags</TagSectionTitle>
                        {userTags
                          .filter(tag => !tag.isUserTag)
                          .map(tag => (
                            <TagOption
                              key={tag.value}
                              selected={subscription.tags?.includes(tag.value)}
                              onClick={() => handleTagSelect(tag.value)}
                            >
                              {tag.label}
                            </TagOption>
                          ))}
                      </TagSection>
                    </>
                  ) : (
                    <TagOption>No tags available</TagOption>
                  )}
                </TagsDropdown>
              )}
            </TagsWrapper>
          </FormGroup>

          <ButtonGroup>
            <Button type="button" variant="secondary" onClick={() => navigate('/subscriptions')}>
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </ButtonGroup>
        </Form>
      </Container>
    </Layout>
  );
};

export default EditSubscription; 